import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Chapter, ChapterSummary } from '@/types'

import Link from 'next/link'
import dynamic from 'next/dynamic'

import {
    ArrowLeft,
    ArrowRight,
    Clock,
    FileText,
    Hourglass
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { getChapterBySlug, getChapterSummaries } from '@/lib/chapters'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import RichText from '@/components/generals/rich-text'
import BookCover from '@/components/book/book-cover'
import ReadingProgress from '@/components/book/reading-progress'
import { BOOK } from '@/constants/book'

interface ChapterPageProps {
    chapter: Chapter
    next: ChapterSummary | null
    previous: ChapterSummary | null
}

// The PDF reader draws pages in the browser, so it never renders on the server.
const PdfReader = dynamic(() => import('@/components/book/pdf-reader'), {
    ssr: false,
    loading: () => <div className='min-h-[60vh] animate-pulse rounded-xl border border-border bg-muted/70 motion-reduce:animate-none' />
})

const NextChapterCard: FC<{ next: ChapterSummary }> = ({ next }): ReactNode => {
    const title = (
        <span
            className='relative z-10'
        >
            {next.title}
        </span>
    )

    return (
        <div
            className={cn(
                'relative rounded-xl border p-6 sm:p-7',
                next.isAvailable ? 'border-border bg-card transition-colors hover:border-primary/40' : 'border-dashed border-border bg-card/60'
            )}
        >
            <div className='flex flex-wrap items-center gap-3'>
                <span className='text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase'>
                    Next: Chapter <span>{next.number}</span>
                </span>

                {!next.isAvailable && (
                    <Badge variant='accent'>
                        <Hourglass aria-hidden='true' />
                        Coming Soon
                    </Badge>
                )}
            </div>

            <p className='mt-3 font-heading text-2xl leading-snug font-medium tracking-tight'>
                {next.isAvailable
                    ? (
                        <Link href={`/book/${next.slug}`} className='after:absolute after:inset-0 after:rounded-xl'>
                            {title}
                        </Link>
                    )
                    : title}
            </p>

            {next.summary && (
                <p
                    className='relative z-10 mt-2 leading-relaxed text-muted-foreground'
                >
                    {next.summary}
                </p>
            )}
        </div>
    )
}

const ComingSoon: FC<{ chapter: Chapter }> = ({ chapter }): ReactNode => {
    return (
        <section className='mx-auto flex max-w-3xl flex-col items-center px-5 py-24 text-center sm:px-8 sm:py-32'>
            <BookCover size='md' />

            <Badge variant='accent' className='mt-10'>
                <Hourglass aria-hidden='true' />
                Coming Soon
            </Badge>

            <p className='mt-6 text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase'>
                Chapter <span>{chapter.number}</span>
            </p>

            <h1 className='mt-3 font-heading text-4xl leading-tight font-medium tracking-tight text-balance sm:text-5xl'>
                <span>{chapter.title}</span>
            </h1>

            {chapter.summary && (
                <p
                    className='mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground'
                >
                    {chapter.summary}
                </p>
            )}

            <p className='mt-8 text-sm text-muted-foreground'>
                This chapter is still being written. It will appear here as soon as it is ready.
            </p>

            <Link href='/book' className={cn(buttonVariants({ variant: 'outline' }), 'mt-8')}>
                <ArrowLeft />
                Back to the book
            </Link>
        </section>
    )
}

const ChapterPage: FC<ChapterPageProps> = ({ chapter, next, previous }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={chapter.title}
                description={chapter.summary ?? `Chapter ${chapter.number} of ${BOOK.title}.`}
            />

            {!chapter.isAvailable && <ComingSoon chapter={chapter} />}

            {chapter.isAvailable && (
                <>
                    <ReadingProgress targetId='chapter-content' />

                    <article id='chapter-content'>
                        <header className='border-b border-border'>
                            <div className='mx-auto max-w-3xl px-5 pt-12 pb-12 sm:px-8 sm:pt-16 sm:pb-14'>
                                <nav aria-label='Breadcrumb' className='text-sm text-muted-foreground'>
                                    <ol className='flex flex-wrap items-center gap-2'>
                                        <li>
                                            <Link href='/book' className='transition-colors hover:text-primary'>
                                                {BOOK.title}
                                            </Link>
                                        </li>
                                        <li aria-hidden='true'>/</li>
                                        <li aria-current='page'>
                                            Chapter <span>{chapter.number}</span>
                                        </li>
                                    </ol>
                                </nav>

                                <p className='mt-10 text-xs font-semibold tracking-[0.18em] text-primary uppercase'>
                                    Chapter <span>{chapter.number}</span>
                                </p>

                                <h1 className='mt-4 font-heading text-4xl leading-[1.1] font-medium tracking-tight text-balance sm:text-5xl'>
                                    <span>{chapter.title}</span>
                                </h1>

                                {chapter.summary && (
                                    <p
                                        className='mt-6 font-heading text-xl leading-relaxed text-muted-foreground italic'
                                    >
                                        {chapter.summary}
                                    </p>
                                )}

                                {chapter.readingMinutes !== null && (
                                    <p className='mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground'>
                                        <Clock className='size-4' aria-hidden='true' />
                                        <span><span>{chapter.readingMinutes}</span> min read</span>
                                    </p>
                                )}
                            </div>
                        </header>

                        {chapter.pdfUrl && (
                            <div className='mx-auto max-w-5xl px-2 pt-8 sm:px-8 sm:pt-12'>
                                <PdfReader url={chapter.pdfUrl} title={`Chapter ${chapter.number} PDF`} />
                            </div>
                        )}

                        {chapter.body && (
                            <div className='mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16'>
                                <RichText
                                    html={chapter.body}
                                    className='font-heading text-[1.1875rem] leading-[1.75] text-foreground/90'
                                />
                            </div>
                        )}

                        {chapter.pdfUrl && !chapter.body && <div className='pb-12' />}

                        {!chapter.pdfUrl && !chapter.body && (
                            <div className='mx-auto max-w-3xl px-5 py-16 sm:px-8'>
                                <div className='flex flex-col items-center rounded-xl border border-dashed border-border bg-card/60 px-6 py-14 text-center'>
                                    <FileText className='size-6 text-primary' aria-hidden='true' />
                                    <p className='mt-4 font-heading text-2xl font-medium'>The PDF is on its way</p>
                                    <p className='mt-2 max-w-md text-sm leading-relaxed text-muted-foreground'>
                                        This chapter will be readable right here as soon as its PDF is added.
                                    </p>
                                </div>
                            </div>
                        )}
                    </article>

                    <section aria-label='Continue reading' className='mx-auto max-w-3xl px-5 pb-24 sm:px-8'>
                        <div className='border-t border-border pt-12'>
                            {next && <NextChapterCard next={next} />}

                            <div className='mt-8 flex flex-wrap items-center justify-between gap-4'>
                                {previous && previous.isAvailable
                                    ? (
                                        <Link href={`/book/${previous.slug}`} className={buttonVariants({ variant: 'ghost' })}>
                                            <ArrowLeft />
                                            Chapter <span>{previous.number}</span>
                                        </Link>
                                    )
                                    : (
                                        <Link href='/book' className={buttonVariants({ variant: 'ghost' })}>
                                            <ArrowLeft />
                                            All chapters
                                        </Link>
                                    )}

                                <Link href='/games' className={buttonVariants({ variant: 'outline' })}>
                                    Practice with the games
                                    <ArrowRight />
                                </Link>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const chapters = await getChapterSummaries()

        return {
            paths: chapters.map(chapter => ({ params: { slug: chapter.slug } })),
            fallback: 'blocking'
        }
    }
    catch (error) {
        console.error('Could not load chapter paths:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ChapterPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true, revalidate: 60 }

    let chapter: Chapter | null = null
    let chapters: ChapterSummary[] = []

    try {
        [chapter, chapters] = await Promise.all([
            getChapterBySlug(slug),
            getChapterSummaries()
        ])
    }
    catch (error) {
        console.error('Could not load chapter:', error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 10 }
    }

    if (!chapter) return { notFound: true, revalidate: 60 }

    const current: Chapter = chapter
    const index = chapters.findIndex(item => item.id === current.id)

    return {
        props: {
            chapter: current,
            next: index >= 0 ? chapters[index + 1] ?? null : null,
            previous: index > 0 ? chapters[index - 1] ?? null : null
        },
        revalidate: 60
    }
}

export default ChapterPage
